import { IPublicClientApplication } from "@azure/msal-browser";
import {
  addMobileAuthMessageListener,
  clearMobileAuthTokenCache,
  getTokenFromMobileAuthMessage,
  postMobileAuthMessage,
  requestMobileAuthToken,
} from "../../mobileAuthBridge";
import { isTokenValid } from "../tokenValidation";
import { IAuthAdapter, TAuthAdapterCallbacks } from "../types";

export class MobileAuthAdapter implements IAuthAdapter {
  private readonly mobileMsalInstance = createMobileMsalInstance();
  private readonly msalInstance = this.mobileMsalInstance as unknown as IPublicClientApplication;
  private callbacks: TAuthAdapterCallbacks | null = null;
  private cleanup: (() => void) | null = null;
  private currentToken = "";
  private invalidTokenRefreshAttempted = false;

  async initialize(callbacks: TAuthAdapterCallbacks) {
    this.callbacks = callbacks;

    const removeMessageListener = addMobileAuthMessageListener((message: unknown) => {
      const token = getTokenFromMobileAuthMessage(message);

      if (token) void this.processToken(token).catch(callbacks.onError);
    });

    this.cleanup = removeMessageListener;

    postMobileAuthMessage("MOBILE_AUTH_READY");

    const idToken = await requestMobileAuthToken();
    await this.processToken(idToken);
  }

  async login() {
    await this.refreshToken();
  }

  async logout() {
    this.currentToken = "";
    this.mobileMsalInstance.setActiveAccount(null);
    clearMobileAuthTokenCache();
    postMobileAuthMessage("AUTH_LOGOUT");
    this.callbacks?.onLoggedOut();
  }

  async refreshToken() {
    const idToken = await requestMobileAuthToken({ forceRefresh: true });

    await this.processToken(idToken);
    return idToken;
  }

  dispose() {
    this.cleanup?.();
    this.cleanup = null;
    this.callbacks = null;
  }

  private async processToken(idToken: string, hasRetried = false): Promise<void> {
    if (!idToken || idToken === this.currentToken) return;

    if (!isTokenValid(idToken, true)) {
      if (!hasRetried && !this.invalidTokenRefreshAttempted) {
        this.invalidTokenRefreshAttempted = true;
        const refreshedToken = await requestMobileAuthToken({ forceRefresh: true });

        await this.processToken(refreshedToken, true);
        return;
      }

      postMobileAuthMessage("AUTH_TOKEN_INVALID");
      this.callbacks?.onError(new Error("Token mobile invalido ou expirado"));
      return;
    }

    this.invalidTokenRefreshAttempted = false;
    this.currentToken = idToken;

    const account = createMobileAccountFromToken(idToken);

    this.mobileMsalInstance.setActiveAccount(account);

    this.callbacks?.onAuthenticated({
      msalInstance: this.msalInstance,
      idToken,
      isMobileAuth: true,
    });
  }
}

type TJwtClaims = {
  sub?: string;
  emails?: string[];
  email?: string;
  name?: string;
  extension_clientId?: string;
  [key: string]: unknown;
};

type TMobileAccount = {
  homeAccountId: string;
  localAccountId: string;
  username: string;
  name: string;
  clientID: string;
  idTokenClaims: TJwtClaims;
};

type TMobileMsalTokenRequest = {
  forceRefresh?: boolean;
};

function createMobileMsalInstance() {
  let activeAccount: TMobileAccount | null = null;

  const activateToken = (idToken: string) => {
    activeAccount = createMobileAccountFromToken(idToken);
    return activeAccount;
  };

  const clearSession = () => {
    activeAccount = null;
    clearMobileAuthTokenCache();
  };

  return {
    getAllAccounts(): TMobileAccount[] {
      return activeAccount ? [activeAccount] : [];
    },

    getActiveAccount(): TMobileAccount | null {
      return activeAccount;
    },

    setActiveAccount(account: TMobileAccount | null): void {
      activeAccount = account;
    },

    async acquireTokenSilent(request: TMobileMsalTokenRequest = {}) {
      const token = await requestMobileAuthToken({
        forceRefresh: request.forceRefresh === true,
      });
      const account = activateToken(token);

      return { idToken: token, account };
    },

    async loginRedirect(): Promise<void> {
      const token = await requestMobileAuthToken({ forceRefresh: true });
      activateToken(token);
    },

    logoutRedirect(): Promise<void> {
      clearSession();
      postMobileAuthMessage("AUTH_LOGOUT");

      return Promise.resolve();
    },

    removeAccount(): Promise<void> {
      clearSession();

      return Promise.resolve();
    },
  };
}

function createMobileAccountFromToken(idToken: string): TMobileAccount {
  const claims = parseJwt(idToken) || {};
  const accountId = claims.sub || "mobile-account";

  return {
    homeAccountId: accountId,
    localAccountId: accountId,
    username: claims.emails?.[0] || claims.email || claims.name || "mobile-user",
    name: claims.name || "",
    clientID: claims.extension_clientId || "",
    idTokenClaims: claims,
  };
}

function parseJwt(token: string): TJwtClaims | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const claims: unknown = JSON.parse(atob(padded));

    return isRecord(claims) ? (claims as TJwtClaims) : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
